import { bsMath, formatPercent, formatTokenAmount, formatUsd } from "@bridgesplit/utils";
import { Row, Text, TextColor, TextVariant, Tooltip } from "@bridgesplit/ui";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";

import { LoopDisplayType } from "../../common/loop";
import { LoopExecutionDetails } from "./types";
import { WIDE_ROW_STYLE, FORMAT_OPTIONS } from "./formatters";

export function SwapPriceDifferenceDisplay({
    swapAmount,
    executionDetails,
    displayType,
    setDisplayTypeHandler,
    collateralToken,
    principalToken,
    inputTokenPrice,
    principalTokenPrice,
    textVariant,
    executionErrorImpactColor,
    inputToken
}: {
    swapAmount: number | undefined;
    executionDetails: LoopExecutionDetails | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
    collateralToken: TokenListMetadata | undefined;
    principalToken: TokenListMetadata | undefined;
    inputTokenPrice: number | undefined;
    principalTokenPrice: number | undefined;
    textVariant: TextVariant;
    executionErrorImpactColor: TextColor | undefined;
    inputToken: TokenListMetadata | undefined;
}) {
    if (!executionDetails) return <Text>--</Text>;

    if (displayType === LoopDisplayType.UsdCostBasis) {
        // For USD display, combine everything into a single USD value
        const relevantUsdPercentDeviation =
            executionDetails.receiveUsdAmount &&
            collateralToken &&
            bsMath.div(executionDetails.oraclePriceDeviation, executionDetails.receiveUsdAmount);
        const usdPercentDeviation =
            swapAmount &&
            inputTokenPrice &&
            collateralToken &&
            bsMath.div(
                executionDetails.oraclePriceDeviation,
                bsMath.mul(bsMath.div(swapAmount, 10 ** collateralToken.decimals), inputTokenPrice)
            );

        const isPositiveImpact = executionDetails.oraclePriceDeviation <= 0;
        return (
            <Tooltip
                title={
                    isPositiveImpact
                        ? "Current swap price is above fair value."
                        : `Execution price is ${formatPercent(
                              usdPercentDeviation
                          )} worse than expected resulting in a ${formatPercent(
                              relevantUsdPercentDeviation
                          )} lower received amount due to the loop multiplier.`
                }
            >
                <Text
                    variant={textVariant}
                    translate="no"
                    onClick={() => setDisplayTypeHandler()}
                    color={isPositiveImpact ? "success" : executionErrorImpactColor}
                >
                    {formatUsd(-executionDetails.oraclePriceDeviation)}
                </Text>
            </Tooltip>
        );
    }

    // For TokenCostBasis, show the breakdown of collateral impact and principal leftover
    const inputPrice = inputTokenPrice || 1;
    const principalPrice = principalTokenPrice || 1;

    // Calculate the collateral impact (negative oracle price deviation minus leftover principal)
    const totalImpactUsd = -executionDetails.oraclePriceDeviation - executionDetails.leftOverPrincipalUsd;
    const inputImpactAmount = bsMath.div(totalImpactUsd, inputPrice);

    const leftOverPrincipalAmount = bsMath.div(executionDetails.leftOverPrincipalUsd, principalPrice);

    return (
        <Row sx={WIDE_ROW_STYLE} spacing={0.5}>
            <Text
                variant={textVariant}
                translate="no"
                onClick={() => setDisplayTypeHandler()}
                color={inputImpactAmount >= 0 ? "success" : executionErrorImpactColor}
            >
                {formatTokenAmount(inputImpactAmount, {
                    symbol: inputToken?.symbol,
                    ...FORMAT_OPTIONS
                })}
            </Text>
            {leftOverPrincipalAmount > 0 && (
                <Text variant={textVariant} onClick={() => setDisplayTypeHandler()} color="success">
                    {"+ "}
                    {formatTokenAmount(leftOverPrincipalAmount, {
                        symbol: principalToken?.symbol,
                        ...FORMAT_OPTIONS
                    })}
                </Text>
            )}
        </Row>
    );
}
